import { FC, useCallback, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { getCurrency, nullSafeGet } from '../../utils/DataAccessUtils';
import { Button, Card, Col, Divider, message, Row } from 'antd';
import { UserName } from '../name_component/user/UserNameComponent';
import moment from 'moment';
import InvoiceActions from '../common/InvoiceActions';
import InvoicesAdditionalDetails from '../invoices_components/InvoicesAdditionalDetails';
import { optionalFilter } from '../../utils/DataFormatterUtils';
import * as React from 'react';
import InvoiceDraftBanner from '../invoice_draft_banner/InvoiceDraftBanner';
import { isInternalTech } from '../../utils/AuthUtils';
import {
	downloadInvoicePDF,
	updateInvoiceGLCodeForBuyer,
	updateInvoicePONumberForBuyer,
} from '../../thunks/invoices_thunks';
import {
	appendWorkOrderNotesForBuyer,
	appendWorkOrderNotesForSupplier,
} from '../../thunks/work_orders_thunks';
import InvoiceUploadButton from '../invoice_upload_button/InvoiceUploadButton';
import { ROLE_TYPES } from '../../utils/DataConstants';
import DateTimeHover from '../date_time_component/DateTime';
import EditGLCodeForm from '../edit_gl_code_form/EditGLCodeForm';
import EditPONumberForm from '../edit_po_number_form/EditPONumberForm';
import InvoiceEditButton from '../invoice_edit_button/InvoiceEditButton';
import InvoiceDetailsDisplay from './InvoiceDetailsDisplay';
import {
	getInvoiceSubUrl,
	getInvoiceRestCrudThunksContractorId,
	isClientInvoiceView,
} from '../invoice_utilities/InvoiceUtilities';

const style = require('./InvoicePageDisplay.less');

const InvoicePageDisplay: FC<any> = ({
	invoice,
	refreshInvoice,
	showViewInvoicePage = false,
	invoicesUpdating = false,
	isClientView = false,

	currentUser,
	history,
	userType,

	updateInvoice,
	appendWorkOrderNotes,
	updatePONumber,
	updateGLCode,
	updateContractorInvoice,

	setAddingMarkup,
	setEditingInvoice,
	setCopyInvoice,
	otherInvoice = {},
	otherInvoiceLoading = false,
}) => {
	const [generatingPDF, setGeneratingPDF] = useState(false);
	const [editPONumberFormVisible, setEditPONumberFormVisible] = useState(false);
	const [editGLCodeFormVisible, setEditGLCodeFormVisible] = useState(false);
	const formRefs = {
		editGLCodeForm: null,
		editPONumberForm: null,
	};
	const saveFormRef = (formName) => (formRef) => {
		formRefs[formName] = formRef;
	};

	const isBuyerOrInternalTech = useMemo(
		() => userType === ROLE_TYPES.BUYER || isInternalTech(currentUser),
		[userType, currentUser]
	);
	const workOrder = useMemo(() => nullSafeGet('workOrder', invoice), [invoice]);
	const hasContractorId = useMemo(() => nullSafeGet('contractorWorkOrderId', invoice), [invoice]);
	const invoiceId = useMemo(() => nullSafeGet('id', invoice), [invoice]);
	const isBuyerInvoice = useMemo(() => invoice.isBuyerUploaded || false, [invoice]);
	const currency = useMemo(
		() => getCurrency({ workOrder: nullSafeGet('workOrder', invoice) }),
		[invoice]
	);

	const generateInvoicePDF = useCallback(
		(id) => {
			setGeneratingPDF(true);
			downloadInvoicePDF(id, userType, currentUser, invoice)
				.catch(() => {
					message.error('An error occurred in downloading the invoice. Please try again!');
				})
				.finally(() => {
					setGeneratingPDF(false);
				});
		},
		[downloadInvoicePDF, userType, currentUser, invoice]
	);

	const handleInvoicePDFUploadChange = useCallback(
		(invoice) => (invoiceFileUrl) => {
			const newInvoice = {
				...invoice,
				invoicePDFLink: invoiceFileUrl,
			};
			updateInvoice(newInvoice, hasContractorId)
				.catch(() => {
					message.error('An error occurred in uploading the invoice. Please try again!');
				})
				.then(() => {
					refreshInvoice();
				});
		},
		[updateInvoice, refreshInvoice]
	);

	const handleEditPONumberSubmit = useCallback(() => {
		const form = formRefs['editPONumberForm'].props.form;
		form.validateFields((err, values) => {
			if (err) {
				return;
			}
			const poNumber = {
				...values,
			};
			updatePONumber(poNumber)
				.catch(() => {
					message.error('An error occurred updating the PO Number. Please try again!');
				})
				.then(() => {
					setEditPONumberFormVisible(false);
					form.resetFields();
				});
		});
	}, [formRefs, updatePONumber]);

	const handleEditGLCodeSubmit = useCallback(() => {
		const form = formRefs['editGLCodeForm'].props.form;
		form.validateFields((err, values) => {
			if (err) {
				return;
			}

			const glCode = {
				...values,
			};

			updateGLCode(glCode)
				.catch(() => {
					message.error('An error occurred updating the GL Code. Please try again!');
				})
				.then(() => {
					setEditGLCodeFormVisible(false);
					form.resetFields();
				});
		});
	}, [formRefs, updateGLCode]);

	const buyerAndInternalSupplierPassThroughItems = useMemo(
		() => [
			...(nullSafeGet('workOrder.lastServiceCall.checkOutTime', invoice)
				? [
						{
							key: 'Service Completed On',
							value: (
								<DateTimeHover
									timestamp={nullSafeGet('workOrder.lastServiceCall.checkOutTime', invoice)}
									showDate={true}
									showTime={false}
									timeZone={nullSafeGet('location.timeZoneId', invoice)}
								/>
							),
						},
				  ]
				: []),
			{
				key: 'GL Code',
				value: (
					<div>
						{optionalFilter(invoice.glCode)}
						{userType === ROLE_TYPES.BUYER ? (
							<Button onClick={() => setEditGLCodeFormVisible(true)} size="large" type="link">
								<i className="icons8-font icons8-edit">Edit</i>
							</Button>
						) : null}
					</div>
				),
			},
			{
				key: 'PO Number',
				value: (
					<div>
						{optionalFilter(invoice.poNumber)}
						{userType === ROLE_TYPES.BUYER ? (
							<Button onClick={() => setEditPONumberFormVisible(true)} size="large" type="link">
								<i className="icons8-font icons8-edit">Edit</i>
							</Button>
						) : null}
					</div>
				),
			},
			invoice.createdByContact
				? {
						key: 'Submitted By',
						value: (
							<UserName contact={invoice.createdByContact} mode={'inline'} placement={'top'} />
						),
				  }
				: {},
			invoice.approvedByContact
				? {
						key: 'Approved By',
						value: (
							<UserName contact={invoice.approvedByContact} mode={'inline'} placement={'top'} />
						),
				  }
				: {},
			invoice.approvedAt
				? {
						key: 'Approved On',
						value: (
							<DateTimeHover
								timestamp={nullSafeGet('approvedAt', invoice)}
								showDate={true}
								showTime={false}
								timeZone={nullSafeGet('location.timeZoneId', invoice)}
							/>
						),
				  }
				: {},
			invoice.processedByContact
				? {
						key: 'Marked Processing By',
						value: (
							<UserName contact={invoice.processedByContact} mode={'inline'} placement={'top'} />
						),
				  }
				: {},
			invoice.processedAt
				? {
						key: 'Marked Processing On',
						value: (
							<DateTimeHover
								timestamp={nullSafeGet('processedAt', invoice)}
								showDate={true}
								showTime={false}
								timeZone={nullSafeGet('location.timeZoneId', invoice)}
							/>
						),
				  }
				: {},
			invoice.markedPaidByContact
				? {
						key: 'Marked Paid By',
						value: (
							<UserName contact={invoice.markedPaidByContact} mode={'inline'} placement={'top'} />
						),
				  }
				: {},
			invoice.markedPaidAt
				? {
						key: 'Marked Paid On',
						value: (
							<DateTimeHover
								timestamp={nullSafeGet('markedPaidAt', invoice)}
								showDate={true}
								showTime={false}
								timeZone={nullSafeGet('location.timeZoneId', invoice)}
							/>
						),
				  }
				: {},
		],
		[invoice]
	);

	const lastDisputeNote = useMemo(
		() => invoice.disputeNotes && invoice.disputeNotes.slice(-1)[0],
		[invoice]
	);
	const disputeNote = useMemo(
		() =>
			lastDisputeNote ? (
				<Col>
					<div>
						<span style={{ fontSize: 16 }}>
							Disputed by{' '}
							<UserName
								mode={'inline'}
								contact={nullSafeGet(`noteAddedByContact`, lastDisputeNote)}
							/>{' '}
							{moment(nullSafeGet(`noteAddedAt`, lastDisputeNote)).fromNow()}
						</span>
					</div>
					<div
						style={{
							display: 'inline-flex',
							background: '#fafafa',
							color: 'rgba(0, 0, 0, 0.65)',
							borderRadius: '30px',
							padding: '8px 16px',
						}}
					>
						<span
							style={{
								fontSize: 16,
							}}
						>
							{nullSafeGet(`text`, lastDisputeNote)}
						</span>
					</div>
					{!isBuyerOrInternalTech ? (
						<div className={'mt-2 flex gap-4'}>
							<Button
								type="primary"
								key="Edit"
								onClick={() => history.push(`/${userType}/invoices/edit/${invoiceId}`)}
							>
								Edit Invoice
							</Button>
							<Button
								key="Note"
								onClick={() =>
									history.push(`/${userType}/workOrders/detail/${invoice.workOrderId}/notes`)
								}
							>
								Send a note
							</Button>
						</div>
					) : null}
				</Col>
			) : null,
		[history, invoice, invoiceId, isBuyerOrInternalTech, lastDisputeNote, userType]
	);
	const path = getInvoiceSubUrl(userType, currentUser, invoice);

	return (
		<Row style={{ margin: '16px 8px' }} gutter={12} key={invoice.id}>
			{isBuyerOrInternalTech
				? [
						<EditGLCodeForm
							formData={invoice}
							wrappedComponentRef={saveFormRef('editGLCodeForm')}
							visible={editGLCodeFormVisible}
							confirming={invoicesUpdating}
							onCancel={() => setEditGLCodeFormVisible(false)}
							onSubmit={handleEditGLCodeSubmit}
						/>,
						<EditPONumberForm
							formData={invoice}
							wrappedComponentRef={saveFormRef('editPONumberForm')}
							visible={editPONumberFormVisible}
							confirming={invoicesUpdating}
							onCancel={() => setEditPONumberFormVisible(false)}
							onSubmit={handleEditPONumberSubmit}
						/>,
				  ]
				: null}
			<Col span={24}>
				<Card>
					{showViewInvoicePage ? (
						<Row justify={'space-between'}>
							<Col />
							<Col>
								<Button
									onClick={() => {
										history.push(`/${path}/detail/${invoice.id}`);
									}}
								>
									View Invoice Page
								</Button>
								<InvoiceEditButton invoice={invoice} />
							</Col>
						</Row>
					) : null}
					<InvoiceActions
						invoice={invoice}
						refreshInvoice={refreshInvoice}
						updateInvoice={updateInvoice}
						style={style}
						appendWorkOrderNotes={appendWorkOrderNotes}
						showEditAndViewButton={false}
						isClientView={isClientView}
						setAddingMarkup={setAddingMarkup}
						setEditingInvoice={setEditingInvoice}
						setCopyInvoice={setCopyInvoice}
						otherInvoiceLoading={otherInvoiceLoading}
						otherInvoice={otherInvoice}
					/>
					<InvoiceDraftBanner
						invoice={invoice}
						generateInvoicePDF={generateInvoicePDF}
						handleInvoicePDFUploadChange={handleInvoicePDFUploadChange}
						generatingPDF={generatingPDF}
					/>
					<InvoicesAdditionalDetails
						invoice={invoice}
						passthroughItems={[
							<InvoiceUploadButton
								handleInvoicePDFUploadChange={handleInvoicePDFUploadChange}
								invoice={invoice}
								generatingPDF={generatingPDF}
								generateInvoicePDF={generateInvoicePDF}
							/>,
							{
								key: 'File',
								value: (
									<InvoiceUploadButton
										handleInvoicePDFUploadChange={handleInvoicePDFUploadChange}
										invoice={invoice}
										generatingPDF={generatingPDF}
										generateInvoicePDF={generateInvoicePDF}
									/>
								),
								className: 'detailRow__item firstRow lastCol',
							},
							{
								key: 'Submitted By Company',
								value: isBuyerInvoice
									? nullSafeGet('buyerCompany.displayName', workOrder)
									: nullSafeGet('supplierFacility.displayName', workOrder),
							},
							{
								key: 'Submitted On',
								value: (
									<DateTimeHover
										timestamp={nullSafeGet('createdAt', invoice)}
										showDate={true}
										showTime={false}
										timeZone={nullSafeGet('location.timeZoneId', invoice)}
									/>
								),
							},
							...(nullSafeGet('workOrder.plannedMaintenanceSchedule.supplierContractId', invoice)
								? [
										{
											key: 'Contract ID',
											value: nullSafeGet(
												'workOrder.plannedMaintenanceSchedule.supplierContractId',
												invoice
											),
										},
								  ]
								: []),
							...(isBuyerOrInternalTech ? buyerAndInternalSupplierPassThroughItems : []),
						]}
					/>
					<Divider />
					<InvoiceDetailsDisplay invoice={invoice} currency={currency} />
				</Card>
			</Col>
		</Row>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateInvoice: (entity, hasContractorId) =>
		dispatch(
			getInvoiceRestCrudThunksContractorId(
				ownProps.userType,
				hasContractorId,
				isClientInvoiceView(entity, ownProps.currentUser)
			).update(entity)
		),
	appendWorkOrderNotes: (entity) =>
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? dispatch(appendWorkOrderNotesForSupplier(entity))
			: dispatch(appendWorkOrderNotesForBuyer(entity)),
	updateGLCode: (entity) => dispatch(updateInvoiceGLCodeForBuyer(entity)),
	updatePONumber: (entity) => dispatch(updateInvoicePONumberForBuyer(entity)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(InvoicePageDisplay)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		currentUser: (state as any).session.currentUser,
	}),
	() => ({})
)(ComponentWithoutUserType);
